/**
 * Dealer (Bayi) first-class entity fixture (ADR #021 — 2026-04-22).
 *
 * Bayi bir partner'a atanır (PartnerId FK). Cihazlar da doğrudan bayiye atanır
 * (`device-dealers.ts` bridge fixture'ı ile). Böylece cihaz → bayi → partner
 * zinciri deterministik olarak kurulur ve eski UserPartner türetmesi fallback
 * kalır (mevcut kod bozulmaz).
 *
 * Seed: 8 partner arasına 20 bayi dağılır; 4'ü hasLogin=true.
 */

import { MOCK_PARTNERS } from "./partners"

export interface Dealer {
  id: string
  partnerId: string
  name: string
  contactEmail: string
  contactPhone: string | null
  country: string
  city: string
  createdAt: string
  isActive: boolean
  hasLogin: boolean
  /** Plaintext mock password; real backend hash'leyecek. */
  password: string | null
}

const feb10 = "2026-02-10T09:00:00Z"
const marIso = "2026-03-15T10:00:00Z"
const aprIso = "2026-04-08T12:00:00Z"
const earlyApr = "2026-04-20T08:00:00Z"

type SeedInput = Omit<Dealer, "id"> & { slug: string }

const partnerIds = MOCK_PARTNERS.map((p) => p.id)

function pid(index: number): string {
  const id = partnerIds[index % partnerIds.length]
  if (!id) {
    throw new Error("Partner fixture empty; cannot seed dealers")
  }
  return id
}

const seedInputs: SeedInput[] = [
  // partner-north (Kuzey Enerji Çözümleri)
  {
    slug: "samsun-merkez",
    partnerId: pid(0),
    name: "Samsun Merkez Bayii",
    contactEmail: "bayi@samsunmerkez.com",
    contactPhone: "+90 362 444 0001",
    country: "Türkiye",
    city: "Samsun",
    createdAt: feb10,
    isActive: true,
    hasLogin: true,
    password: "dealer123",
  },
  {
    slug: "ordu-enerji",
    partnerId: pid(0),
    name: "Ordu Enerji Hizmetleri",
    contactEmail: "info@orduenerji.com.tr",
    contactPhone: "+90 452 444 0023",
    country: "Türkiye",
    city: "Ordu",
    createdAt: marIso,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  {
    slug: "trabzon-servis",
    partnerId: pid(0),
    name: "Trabzon Teknik Servis",
    contactEmail: "servis@trabzonteknik.com",
    contactPhone: "+90 462 444 0045",
    country: "Türkiye",
    city: "Trabzon",
    createdAt: aprIso,
    isActive: false,
    hasLogin: false,
    password: null,
  },
  // partner-aegean (Ege Solar Partners)
  {
    slug: "izmir-bornova",
    partnerId: pid(1),
    name: "İzmir Bornova Bayii",
    contactEmail: "bornova@egesolar.com",
    contactPhone: "+90 232 444 0067",
    country: "Türkiye",
    city: "İzmir",
    createdAt: feb10,
    isActive: true,
    hasLogin: true,
    password: "dealer123",
  },
  {
    slug: "aydin-ges",
    partnerId: pid(1),
    name: "Aydın GES Kurulum",
    contactEmail: "destek@aydinges.com",
    contactPhone: "+90 256 444 0089",
    country: "Türkiye",
    city: "Aydın",
    createdAt: marIso,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  {
    slug: "manisa-solar",
    partnerId: pid(1),
    name: "Manisa Solar Sistemleri",
    contactEmail: "info@manisasolar.com",
    contactPhone: "+90 236 444 0112",
    country: "Türkiye",
    city: "Manisa",
    createdAt: earlyApr,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  // partner-south (Akdeniz Grid Operations)
  {
    slug: "antalya-merkez",
    partnerId: pid(2),
    name: "Antalya Merkez Bayii",
    contactEmail: "antalya@akdenizgrid.com.tr",
    contactPhone: "+90 242 444 0134",
    country: "Türkiye",
    city: "Antalya",
    createdAt: feb10,
    isActive: true,
    hasLogin: true,
    password: "dealer123",
  },
  {
    slug: "mugla-enerji",
    partnerId: pid(2),
    name: "Muğla Enerji Hizmetleri",
    contactEmail: "info@muglaenerji.com",
    contactPhone: "+90 252 444 0156",
    country: "Türkiye",
    city: "Muğla",
    createdAt: aprIso,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  // partner-central (İç Anadolu EMS)
  {
    slug: "ankara-cankaya",
    partnerId: pid(3),
    name: "Ankara Çankaya Bayii",
    contactEmail: "cankaya@icanadolu.com.tr",
    contactPhone: "+90 312 444 0178",
    country: "Türkiye",
    city: "Ankara",
    createdAt: marIso,
    isActive: true,
    hasLogin: true,
    password: "dealer123",
  },
  {
    slug: "konya-teknik",
    partnerId: pid(3),
    name: "Konya Teknik Bayii",
    contactEmail: "destek@konyateknik.com",
    contactPhone: "+90 332 444 0199",
    country: "Türkiye",
    city: "Konya",
    createdAt: aprIso,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  {
    slug: "kayseri-ems",
    partnerId: pid(3),
    name: "Kayseri EMS Servis",
    contactEmail: "servis@kayseriems.com",
    contactPhone: null,
    country: "Türkiye",
    city: "Kayseri",
    createdAt: earlyApr,
    isActive: false,
    hasLogin: false,
    password: null,
  },
  // partner-marmara (Marmara Enerji Sistemleri)
  {
    slug: "istanbul-anadolu",
    partnerId: pid(4),
    name: "İstanbul Anadolu Bayii",
    contactEmail: "anadolu@marmaraenerji.com",
    contactPhone: "+90 216 444 0201",
    country: "Türkiye",
    city: "İstanbul",
    createdAt: feb10,
    isActive: true,
    hasLogin: true,
    password: "dealer123",
  },
  {
    slug: "istanbul-avrupa",
    partnerId: pid(4),
    name: "İstanbul Avrupa Bayii",
    contactEmail: "avrupa@marmaraenerji.com",
    contactPhone: "+90 212 444 0223",
    country: "Türkiye",
    city: "İstanbul",
    createdAt: marIso,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  {
    slug: "kocaeli-sistem",
    partnerId: pid(4),
    name: "Kocaeli Sistem Teknik",
    contactEmail: "info@kocaelisistem.com",
    contactPhone: "+90 262 444 0245",
    country: "Türkiye",
    city: "Kocaeli",
    createdAt: aprIso,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  // partner-east (Doğu Şebeke Teknolojileri)
  {
    slug: "erzurum-bayii",
    partnerId: pid(5),
    name: "Erzurum Bayii",
    contactEmail: "bayi@erzurumses.com",
    contactPhone: "+90 442 444 0267",
    country: "Türkiye",
    city: "Erzurum",
    createdAt: earlyApr,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  {
    slug: "van-enerji",
    partnerId: pid(5),
    name: "Van Enerji Servis",
    contactEmail: "servis@vanenerji.com",
    contactPhone: "+90 432 444 0289",
    country: "Türkiye",
    city: "Van",
    createdAt: earlyApr,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  // partner-blacksea (Karadeniz Smart Grid)
  {
    slug: "rize-teknik",
    partnerId: pid(6),
    name: "Rize Teknik Hizmet",
    contactEmail: "rize@ksgrid.com.tr",
    contactPhone: "+90 464 444 0301",
    country: "Türkiye",
    city: "Rize",
    createdAt: marIso,
    isActive: true,
    hasLogin: true,
    password: "dealer123",
  },
  {
    slug: "giresun-enerji",
    partnerId: pid(6),
    name: "Giresun Enerji Dağıtım",
    contactEmail: "info@giresunenerji.com",
    contactPhone: null,
    country: "Türkiye",
    city: "Giresun",
    createdAt: aprIso,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  // partner-thrace (Trakya Enerji Dağıtım)
  {
    slug: "edirne-merkez",
    partnerId: pid(7),
    name: "Edirne Merkez Bayii",
    contactEmail: "merkez@trakyaenerji.com",
    contactPhone: "+90 284 444 0323",
    country: "Türkiye",
    city: "Edirne",
    createdAt: earlyApr,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  {
    slug: "tekirdag-solar",
    partnerId: pid(7),
    name: "Tekirdağ Solar Çözümleri",
    contactEmail: "destek@tekirdagsolar.com",
    contactPhone: "+90 282 444 0345",
    country: "Türkiye",
    city: "Tekirdağ",
    createdAt: earlyApr,
    isActive: false,
    hasLogin: false,
    password: null,
  },
]

const seedDealers: Dealer[] = seedInputs.map((input, index) => {
  const { slug, ...rest } = input
  return { id: `dealer-${slug}-${index + 1}`, ...rest }
})

const dealerStore: Dealer[] = seedDealers.map((d) => ({ ...d }))

export function getAllDealers(): Dealer[] {
  return dealerStore.map((d) => ({ ...d }))
}

export function getDealersByPartnerId(partnerId: string): Dealer[] {
  return dealerStore.filter((d) => d.partnerId === partnerId).map((d) => ({ ...d }))
}

export function getDealerById(id: string): Dealer | null {
  const found = dealerStore.find((d) => d.id === id)
  return found ? { ...found } : null
}

// ADR #034 — CRUD mutator'ları (createMockDealer/updateMockDealer/
// deleteMockDealer/toggleDealerActive/findDealerByEmail) kaldırıldı: tek
// tüketicileri emekli edilen SuperAdmin/dealers handler'ıydı. Bu dosya artık
// yalnız SEED kaynağıdır — runtime mutasyon org store'da (organizations.ts).
